import { SelectOptions } from '@spo/logic';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, {
  ActionMeta,
  InputActionMeta,
  MultiValue as mv,
} from 'react-select';
import { useSearchedSports } from '../../../hooks/useSports';
import { metaActions } from '../../../utils/metaActions';
import { selectFilterStyles } from '../common/layout';
import { concatOptions, sortOptions } from '../common/options';
import { IconOption, MultiValue } from '../custom/CustomSelectInput';
import { SportsSearchInputProps } from './SportsSearchInputProps';

export const SportsSearchInput = ({
  onChange,
  selectedSportsOptions = [],
}: SportsSearchInputProps) => {
  const { t } = useTranslation();

  const [sportsOptions, setSportsOptions] = useState<SelectOptions[]>([]);
  const [selectedSports, setSelectedSports] =
    useState<mv<SelectOptions> | null>(null);
  const [searchedSport, setSearchedSport] = useState<string>('');

  const {
    data: searchedSportsData,
    isError: searchedSportsIsError,
    isFetching: searchedSportsIsFetching,
  } = useSearchedSports(searchedSport, searchedSport.length > 0);

  const setOptionsSportsAndSelectedSports = (
    sportsListOptions: SelectOptions[],
  ) => {
    const sortedSportsOptions = sortOptions([], sportsListOptions);

    setSportsOptions(sortedSportsOptions);

    selectedSportsOptions.length > 0 &&
      setSelectedSports([...new Set(selectedSportsOptions)]);
  };

  useEffect(() => {
    if (searchedSportsData && searchedSportsData.length > 0) {
      const sportsListOptions: SelectOptions[] = searchedSportsData?.map(
        (sports) => ({
          label: `${sports.id} - ${sports.name}`,
          value: sports.id,
          name: `${sports.name}`,
        }),
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOptionsSportsAndSelectedSports(sportsListOptions);
    }
  }, [searchedSportsData]);

  useEffect(() => {
    if (searchedSportsIsError) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSportsOptions([
        {
          label: t(`select.loading_error`) ?? '',
          value: NaN,
          name: '',
        },
      ]);
    }
  }, [searchedSportsIsError]);

  useEffect(() => {
    if (selectedSportsOptions.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOptionsSportsAndSelectedSports(selectedSportsOptions);
    }
  }, []);

  const onInputChange = (search: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === metaActions.inputChange) {
      setSearchedSport(search);
    }
  };

  const handleChange = async (
    sports: mv<SelectOptions>,
    actionMeta: ActionMeta<SelectOptions>,
  ) => {
    const sortedSportsOptions = sortOptions(sports, sportsOptions);
    setSportsOptions(sortedSportsOptions);

    const allSelectedSports = concatOptions(actionMeta, selectedSports, sports);

    onChange(allSelectedSports);
    setSelectedSports(allSelectedSports);
  };

  return (
    <div className="vtmn-w-full" data-testid="sports_input_search">
      <span className="vtmn-typo_text-2">{t('input.sport_label')}</span>
      <Select
        data-testid="sports-search-input"
        aria-label={
          t(`select.sports_placeholder`) || 'Add one or several sports'
        }
        className="vtmn-mt-2"
        styles={selectFilterStyles}
        placeholder={t(`select.sports_placeholder`)}
        isMulti
        value={selectedSports}
        onChange={handleChange}
        onInputChange={onInputChange}
        isSearchable
        hideSelectedOptions={false}
        options={sportsOptions}
        isLoading={searchedSportsIsFetching}
        closeMenuOnSelect={false}
        components={{
          Option: IconOption,
          MultiValue,
        }}
        id="sports-filter"
      />
    </div>
  );
};
